// csv.js - CSV utilities
// Export participant info to CSV and trigger browser download

/**
 * Escape a single CSV cell value by quoting when needed and doubling internal quotes.
 * @param {any} cell
 * @returns {string}
 */
const escapeCsvCell = (cell) => {
  const value = cell == null ? '' : String(cell)
  if (/[",\n]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`
  }
  return value
}

/**
 * Build CSV content string from headers and rows.
 * @param {string[]} headers
 * @param {Array<Array<string>>} rows
 * @returns {string}
 */
const buildCsvContent = (headers, rows) => {
  return [headers, ...rows].map((r) => r.map(escapeCsvCell).join(',')).join('\n')
}

/**
 * Download a CSV content as a file.
 * @param {string} csvContent
 * @param {string} filename
 */
const downloadCsv = (csvContent, filename) => {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', filename)
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

/**
 * Export participants to CSV using user details from useParticipants hook.
 * @param {Array<Object>} participantUsers - Array of user objects (from useParticipants), may include familyId
 * @param {Array<Object>} participants - Raw participants from session (id, familyId, note)
 * @param {Object} options
 * @param {string} [options.filenameBase='session'] - Base of filename
 * @param {string|Date} [options.date] - Date to include in filename
 */
export const exportParticipantsToCsv = (participantUsers = [], participants = [], options = {}) => {
  try {
    const { filenameBase = 'session', date } = options
    const headers = ['Name', 'Email', 'Phone', 'WeChat', 'Emergency Name', 'Emergency Phone', 'Note']

    const participantNoteMap = new Map()
    ;(participants || []).forEach((p) => {
      const key = `${p.id}-${p.familyId || 0}`
      participantNoteMap.set(key, p.note || '')
    })

    const rows = (participantUsers || []).map((u) => {
      const name = u?.name || `${u?.firstName || ''} ${u?.lastName || ''}`.trim()
      const email = u?.email || ''
      const phone = u?.phoneNumber || ''
      const wechat = u?.weChatId || ''
      const emergencyName = u?.emergencyContact?.name || ''
      const emergencyPhone = u?.emergencyContact?.phone || ''
      const key = `${u?.resourceId}-${u?.familyId || 0}`
      const note = participantNoteMap.get(key) || ''
      return [name, email, phone, wechat, emergencyName, emergencyPhone, note]
    })

    const csvContent = buildCsvContent(headers, rows)
    const safeTitle = (filenameBase || 'session').replace(/[^a-z0-9-_]+/gi, '_')
    const datePart = typeof date === 'string' ? date.toString().slice(0, 10) : date instanceof Date ? date.toISOString().slice(0, 10) : ''
    const filename = `${safeTitle}${datePart ? `_${datePart}` : ''}_participants.csv`
    downloadCsv(csvContent, filename)
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('Failed to export participants CSV:', err)
  }
}

const CsvUtils = { exportParticipantsToCsv }
export default CsvUtils
